import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { format } from 'date-fns';
import { ProcurementFormFields } from './ProcurementFormFields';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';
import { useProcurementForm } from './hooks/useProcurementForm';

const REQUIRED_FIELD_PATHS: { [key: string]: string } = {
  'source_type': 'source-section',
  'source_id': 'source-section',
  'items.0.product_name': 'items-section',
  'items.0.quantity': 'items-section',
  'items.0.price_per_unit': 'items-section',
  'expected_delivery_date': 'expected-date-section',
};

const ProcurementForm = () => {
  const [searchParams] = useSearchParams();
  const harvestId = searchParams.get('harvestId');
  
  const { form, harvest, isLoadingHarvest, onSubmit, isSubmitting } = useProcurementForm(harvestId);

  // Refs for major form sections
  const sourceSectionRef = React.useRef<HTMLDivElement>(null);
  const itemsSectionRef = React.useRef<HTMLDivElement>(null);
  const dateSectionRef = React.useRef<HTMLDivElement>(null);

  // Map for quick lookup
  const sectionRefs: Record<string, React.RefObject<HTMLDivElement>> = {
    'source-section': sourceSectionRef,
    'items-section': itemsSectionRef,
    'expected-date-section': dateSectionRef,
  };

  // Focus/scroll improvements on error
  React.useEffect(() => {
    if (!form.formState.isSubmitted || Object.keys(form.formState.errors).length === 0) {
      return;
    }

    // Flatten errors and check required field order priority
    const errorFieldPaths: string[] = [];
    const collectErrorPaths = (errorsObj: any, prefix = '') => {
      for (const key in errorsObj) {
        const value = errorsObj[key];
        const path = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'object' && !value?.message && value !== null) {
          collectErrorPaths(value, path);
        } else if (value) {
          errorFieldPaths.push(path);
        }
      }
    };
    collectErrorPaths(form.formState.errors);

    // Find first important required field with error
    const requiredFieldOrder = [
      'source_type', 'source_id',
      'items.0.product_name',
      'items.0.quantity',
      'items.0.price_per_unit',
      'expected_delivery_date',
    ];
    let fieldToFocus: string | null = null;
    for (const requiredPath of requiredFieldOrder) {
      if (errorFieldPaths.some(path => path === requiredPath || path.startsWith(requiredPath))) {
        fieldToFocus = requiredPath;
        break;
      }
    }
    if (!fieldToFocus) {
      // fallback to first error
      fieldToFocus = errorFieldPaths[0];
    }

    // Focus input
    if (fieldToFocus) {
      // Try to focus the specific input
      let element = document.querySelector(`[name="${fieldToFocus}"]`);
      if (!element && fieldToFocus.startsWith('items.0.')) {
        // items[0] fields can sometimes be like items[0].product_name
        const baseName = fieldToFocus.replace('items.0.', 'items[0].');
        element = document.querySelector(`[name="${baseName}"]`);
      }
      if (element && typeof (element as HTMLElement).focus === 'function') {
        (element as HTMLElement).focus();
      }

      // Also scroll the relevant section into view
      const sectionKey = REQUIRED_FIELD_PATHS[fieldToFocus];
      if (sectionKey && sectionRefs[sectionKey]?.current) {
        sectionRefs[sectionKey]!.current!.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [form.formState.isSubmitted, form.formState.errors]);

  if (isLoadingHarvest && harvestId) {
    return <ProcurementLoadingSkeleton />;
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">Start Sourcing</h2>
          {harvest && (
            <p className="text-muted-foreground">
              Creating procurement for {harvest.expected_quantity}kg harvest expected on{' '}
              {harvest.expected_date ? format(new Date(harvest.expected_date), 'PPP') : 'N/A'}
            </p>
          )}
          {!harvestId && (
            <p className="text-muted-foreground">
              Create a new procurement from a farm, supplier, or agent
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div ref={sourceSectionRef} id="source-section">
              <ProcurementFormFields form={form} />
            </div>
            {/* The items and date fields are rendered by ProcurementFormFields — so we wrap them */}
            <div ref={itemsSectionRef} id="items-section" />
            <div ref={dateSectionRef} id="expected-date-section" />

            <div className="flex justify-end gap-4 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="min-w-[200px]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Start Sourcing Process'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

const ProcurementLoadingSkeleton = () => (
  <Card className="w-full">
    <CardHeader>
      <Skeleton className="h-8 w-3/4" />
    </CardHeader>
    <CardContent className="space-y-4">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </CardContent>
  </Card>
);

export default ProcurementForm;
